import React, { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import Select from 'react-select';
import type {
  FairAssessment, QualitativeInputs, QuantitativeInputs, RatingLevel,
} from '../types/fair';
import { EMPTY_FAIR_ASSESSMENT, EMPTY_QUALITATIVE } from '../types/fair';
import {
  RATING_LABELS, RATING_ORDER, calcQualitativeResults, runMonteCarlo,
  deriveQuantitativeFromQualitative, formatCurrency, FAIR_TOOLTIPS,
  FAIR_TO_LIKELIHOOD, FAIR_TO_IMPACT,
} from '../utils/fair';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import type { Asset } from '../types/models';

type ThreatOption = { threatId: string; fields?: { threat_name?: string } };

type Props = {
  value: FairAssessment | null | undefined;
  onChange: (next: FairAssessment | null) => void;
  onApplyToRisk?: (likelihood: string, impact: string) => void;
  readOnly?: boolean;
  assets?: Asset[];
  threats?: ThreatOption[];
};

/** Self-contained hover/focus tooltip - deliberately not the native `title`
 * attribute, which is unreliable across browsers/touch and easy to miss. */
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="fair-info-tooltip position-relative d-inline-flex" tabIndex={0}>
    <i className="ri-information-line np-sub" style={{ fontSize: '0.9rem', cursor: 'help' }}></i>
    <span
      className="fair-info-tooltip-content shadow-sm"
      style={{
        display: 'none',
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '6px',
        width: '280px',
        maxWidth: '80vw',
        whiteSpace: 'pre-line',
        background: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)',
        border: '1px solid var(--bs-border-color)',
        borderRadius: '6px',
        padding: '0.6rem 0.75rem',
        fontSize: '0.8rem',
        fontWeight: 400,
        lineHeight: 1.4,
        zIndex: 20,
      }}
    >
      {text}
    </span>
  </span>
);

type Step = 1 | 2 | 3 | 4 | 5;

const STEP_LABELS: Record<Step, string> = {
  1: 'Scope & Context',
  2: 'Threat & Vulnerability',
  3: 'Loss Magnitude',
  4: 'Qualitative Results',
  5: 'Quantitative (Monte Carlo)',
};

const RATING_SELECT_OPTIONS = RATING_ORDER.map((r) => ({ value: r, label: `${r} - ${RATING_LABELS[r]}` }));

/** Leading icon for the overall-risk banner in Step 4, keyed by the same
 * riskColor tone calcQualitativeResults() already derives. */
const RISK_TONE_ICON: Record<string, string> = {
  success: 'ri-shield-check-line',
  info: 'ri-information-line',
  warning: 'ri-alert-line',
  danger: 'ri-error-warning-line',
  dark: 'ri-alarm-warning-line',
};

const RatingField: React.FC<{
  label: string;
  fieldKey: keyof QualitativeInputs;
  value: RatingLevel;
  onChange: (v: RatingLevel) => void;
  disabled?: boolean;
}> = ({ label, fieldKey, value, onChange, disabled }) => (
  <div className="col-12 col-md-6 col-lg-4 np-field">
    <label className="d-flex align-items-center gap-1">
      {label}
      {FAIR_TOOLTIPS[fieldKey as string] && <InfoTooltip text={FAIR_TOOLTIPS[fieldKey as string]} />}
    </label>
    <select
      className="np-select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as RatingLevel)}
    >
      {RATING_SELECT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const RangeField: React.FC<{
  label: string;
  suffix?: string;
  value: { min: number; mostLikely: number; max: number };
  onChange: (v: { min: number; mostLikely: number; max: number }) => void;
  disabled?: boolean;
}> = ({ label, suffix, value, onChange, disabled }) => (
  <div className="col-12 col-md-6 col-lg-4 np-field">
    <label>{label}{suffix ? ` (${suffix})` : ''}</label>
    <div className="d-flex gap-2">
      <div>
        <small className="np-hint d-block">Min</small>
        <input
          type="number"
          className="np-input np-tabular"
          value={value.min}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, min: parseFloat(e.target.value) || 0 })}
        />
      </div>
      <div>
        <small className="np-hint d-block">Most Likely</small>
        <input
          type="number"
          className="np-input np-tabular"
          value={value.mostLikely}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, mostLikely: parseFloat(e.target.value) || 0 })}
        />
      </div>
      <div>
        <small className="np-hint d-block">Max</small>
        <input
          type="number"
          className="np-input np-tabular"
          value={value.max}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, max: parseFloat(e.target.value) || 0 })}
        />
      </div>
    </div>
  </div>
);

export const FairAssessmentPanel: React.FC<Props> = ({
  value, onChange, onApplyToRisk, readOnly, assets = [], threats = [],
}) => {
  const [step, setStep] = useState<Step>(1);
  const [running, setRunning] = useState(false);

  const enabled = !!value?.enabled;
  const assessment: FairAssessment = value?.enabled ? value : EMPTY_FAIR_ASSESSMENT;

  const toggleEnabled = () => {
    if (enabled) {
      // Turn off but keep the data around in case they re-enable -- don't discard work.
      onChange({ ...assessment, enabled: false });
    } else {
      const next: FairAssessment = {
        ...EMPTY_FAIR_ASSESSMENT,
        ...assessment,
        enabled: true,
        createdAt: assessment.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      onChange(next);
      setStep(1);
    }
  };

  const update = (patch: Partial<FairAssessment>) => {
    onChange({ ...assessment, ...patch, updatedAt: new Date().toISOString() });
  };

  const updateQualitative = (patch: Partial<QualitativeInputs>) => {
    update({ qualitative: { ...(assessment.qualitative || EMPTY_QUALITATIVE), ...patch } });
  };

  const qualResults = useMemo(() => calcQualitativeResults(assessment.qualitative || EMPTY_QUALITATIVE), [assessment.qualitative]);

  const runSimulation = () => {
    setRunning(true);
    setTimeout(() => {
      const quant: QuantitativeInputs = assessment.quantitative
        || deriveQuantitativeFromQualitative(assessment.qualitative || EMPTY_QUALITATIVE, assessment.cfAnchor);
      const monteCarlo = runMonteCarlo(quant, 10000);
      update({
        quantitative: quant,
        results: { ...qualResults, monteCarlo },
      });
      setRunning(false);
    }, 10);
  };

  const applyToRisk = () => {
    const likelihood = FAIR_TO_LIKELIHOOD[qualResults.lef];
    const impact = FAIR_TO_IMPACT[qualResults.totalLoss];
    onApplyToRisk?.(likelihood, impact);
  };

  const histogramSeries = useMemo(() => {
    const mc = assessment.results?.monteCarlo;
    if (!mc) return [];
    return [{ name: 'Simulations', data: mc.histogram }];
  }, [assessment.results?.monteCarlo]);

  return (
    <div className="np-card mb-4">
      <style>{`
        .fair-info-tooltip:hover .fair-info-tooltip-content,
        .fair-info-tooltip:focus .fair-info-tooltip-content,
        .fair-info-tooltip:focus-within .fair-info-tooltip-content {
          display: block !important;
        }
      `}</style>
      <div className="np-card-head d-flex align-items-center justify-content-between">
        <h2>
          <i className="ri-flask-line me-2 text-primary"></i>FAIR Quantitative Assessment
        </h2>
        <div className="d-flex align-items-center gap-2">
          <label className="np-switch mb-0" htmlFor="fair-enabled-toggle">
            <input
              type="checkbox"
              checked={enabled}
              disabled={readOnly}
              onChange={toggleEnabled}
              id="fair-enabled-toggle"
            />
            <span className="np-switch-track"></span>
            <span className="np-switch-thumb"></span>
          </label>
          <label htmlFor="fair-enabled-toggle" className="mb-0 small">
            {enabled ? 'Enabled' : 'Disabled'}
          </label>
        </div>
      </div>

      {!enabled && (
        <div className="np-card-body np-sub small">
          Optional. Turn this on to run a full FAIR (Factor Analysis of Information Risk) analysis for
          this risk -- threat/vulnerability/loss-magnitude factors, a qualitative risk score, and an
          optional Monte Carlo simulation producing an Annual Loss Exposure estimate in euros.
        </div>
      )}

      {enabled && (
        <div className="np-card-body">
          <div className="np-tabs mb-4" style={{ flexWrap: 'wrap' }}>
            {([1, 2, 3, 4, 5] as Step[]).map((s) => (
              <button
                key={s}
                type="button"
                className={`np-tab${step === s ? ' active' : ''}`}
                onClick={() => setStep(s)}
              >
                {s}. {STEP_LABELS[s]}
              </button>
            ))}
          </div>

          {step === 1 && (
            <div className="row g-3">
              <div className="col-12 col-md-6 np-field">
                <label>Asset</label>
                <Select
                  options={assets.map((a) => ({ value: a.assetId, label: a.name }))}
                  value={assets.filter((a) => a.assetId === assessment.assetId)
                    .map((a) => ({ value: a.assetId, label: a.name }))[0] || null}
                  onChange={(option) => update({ assetId: (option as { value: string } | null)?.value || '' })}
                  placeholder="Select the asset in scope..."
                  isDisabled={readOnly}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={reactSelectStyles(readOnly)}
                />
              </div>
              <div className="col-12 col-md-6 np-field">
                <label>Threat Community</label>
                <Select
                  options={threats.map((t) => ({ value: t.threatId, label: t.fields?.threat_name || t.threatId }))}
                  value={threats.filter((t) => t.threatId === assessment.threatId)
                    .map((t) => ({ value: t.threatId, label: t.fields?.threat_name || t.threatId }))[0] || null}
                  onChange={(option) => update({ threatId: (option as { value: string } | null)?.value || '' })}
                  placeholder="Select the threat community..."
                  isDisabled={readOnly}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={reactSelectStyles(readOnly)}
                />
              </div>
              <div className="col-12">
                <small className="np-sub">
                  The asset in scope and the threat community being assessed against it. Both are
                  optional but recommended -- they give this assessment's results context when
                  reviewed later.
                </small>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="row g-3">
              <RatingField label="Contact Frequency" fieldKey="contactFrequency" value={assessment.qualitative.contactFrequency} disabled={readOnly} onChange={(v) => updateQualitative({ contactFrequency: v })} />
              <RatingField label="Probability of Action" fieldKey="probabilityOfAction" value={assessment.qualitative.probabilityOfAction} disabled={readOnly} onChange={(v) => updateQualitative({ probabilityOfAction: v })} />
              <RatingField label="Threat Capability" fieldKey="threatCapability" value={assessment.qualitative.threatCapability} disabled={readOnly} onChange={(v) => updateQualitative({ threatCapability: v })} />
              <RatingField label="Control Strength" fieldKey="controlStrength" value={assessment.qualitative.controlStrength} disabled={readOnly} onChange={(v) => updateQualitative({ controlStrength: v })} />
              <div className="col-12">
                <small className="np-sub">
                  Contact Frequency x Probability of Action = Threat Event Frequency (TEF). Threat
                  Capability vs Control Strength = Vulnerability. TEF x Vulnerability = Loss Event
                  Frequency (LEF).
                </small>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="row g-3">
              <div className="col-12"><h6 className="text-uppercase np-sub small mb-0">Primary Loss</h6></div>
              <RatingField label="Productivity" fieldKey="productivity" value={assessment.qualitative.productivity} disabled={readOnly} onChange={(v) => updateQualitative({ productivity: v })} />
              <RatingField label="Response" fieldKey="response" value={assessment.qualitative.response} disabled={readOnly} onChange={(v) => updateQualitative({ response: v })} />
              <RatingField label="Replacement" fieldKey="replacement" value={assessment.qualitative.replacement} disabled={readOnly} onChange={(v) => updateQualitative({ replacement: v })} />
              <RatingField label="Competitive Advantage" fieldKey="competitiveAdvantage" value={assessment.qualitative.competitiveAdvantage} disabled={readOnly} onChange={(v) => updateQualitative({ competitiveAdvantage: v })} />
              <RatingField label="Fines & Judgements" fieldKey="finesJudgements" value={assessment.qualitative.finesJudgements} disabled={readOnly} onChange={(v) => updateQualitative({ finesJudgements: v })} />
              <RatingField label="Reputation" fieldKey="reputation" value={assessment.qualitative.reputation} disabled={readOnly} onChange={(v) => updateQualitative({ reputation: v })} />
              <div className="col-12 mt-2"><h6 className="text-uppercase np-sub small mb-0">Secondary Loss</h6></div>
              <RatingField label="Secondary LEF" fieldKey="secondaryLef" value={assessment.qualitative.secondaryLef} disabled={readOnly} onChange={(v) => updateQualitative({ secondaryLef: v })} />
              <RatingField label="Secondary Legal" fieldKey="secondaryLegal" value={assessment.qualitative.secondaryLegal} disabled={readOnly} onChange={(v) => updateQualitative({ secondaryLegal: v })} />
              <RatingField label="Secondary Regulatory" fieldKey="secondaryRegulatory" value={assessment.qualitative.secondaryRegulatory} disabled={readOnly} onChange={(v) => updateQualitative({ secondaryRegulatory: v })} />
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="row g-3 mb-4">
                {[
                  ['Threat Event Frequency', qualResults.tef],
                  ['Vulnerability', qualResults.vulnerability],
                  ['Loss Event Frequency', qualResults.lef],
                  ['Primary Loss', qualResults.primaryLoss],
                  ['Secondary Loss', qualResults.secondaryLoss],
                  ['Total Loss Magnitude', qualResults.totalLoss],
                ].map(([label, rating]) => (
                  <div className="col-6 col-md-4 col-lg-2" key={label as string}>
                    <div className="np-mini-stat h-100">
                      <div className="np-mini-stat-value"><span className="np-chip np-tone-neutral">{RATING_LABELS[rating as RatingLevel]}</span></div>
                      <div className="np-mini-stat-label">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className={`np-banner np-tone-${qualResults.riskColor} mb-3`}>
                <i className={RISK_TONE_ICON[qualResults.riskColor] || 'ri-information-line'}></i>
                <div className="flex-grow-1 d-flex align-items-center justify-content-between gap-2">
                  <div>
                    <strong>Overall FAIR Risk Score: <span className="np-tabular">{qualResults.qualitativeScore}</span>/100</strong>
                    <div className="small">{qualResults.qualitativeLabel}</div>
                  </div>
                  {onApplyToRisk && !readOnly && (
                    <button type="button" className="np-btn np-btn-primary np-btn-sm" onClick={applyToRisk}>
                      Apply to Risk's Likelihood/Impact
                    </button>
                  )}
                </div>
              </div>
              {onApplyToRisk && (
                <small className="np-sub">
                  Applying maps Loss Event Frequency &rarr; Likelihood ({FAIR_TO_LIKELIHOOD[qualResults.lef]}) and
                  Total Loss &rarr; Impact ({FAIR_TO_IMPACT[qualResults.totalLoss]}) on this risk, which then
                  feeds the standard inherent-risk calculation.
                </small>
              )}
            </div>
          )}

          {step === 5 && (
            <div>
              <p className="np-sub small">
                Optional: provide min / most-likely / max ranges for each FAIR factor to run a 10,000-
                iteration Monte Carlo simulation producing an Annual Loss Exposure (ALE) estimate in
                euros. Leave as-is to auto-derive starting ranges from your Step 2/3 ratings above.
              </p>
              <div className="row g-3 mb-3">
                {(() => {
                  const q = assessment.quantitative || deriveQuantitativeFromQualitative(assessment.qualitative, assessment.cfAnchor);
                  const setQ = (patch: Partial<QuantitativeInputs>) => update({ quantitative: { ...q, ...patch } });
                  return (
                    <>
                      <RangeField label="Contact Frequency" suffix="per year" value={q.contactFrequencyPerYear} disabled={readOnly} onChange={(v) => setQ({ contactFrequencyPerYear: v })} />
                      <RangeField label="Probability of Action" suffix="0-1" value={q.probabilityOfAction} disabled={readOnly} onChange={(v) => setQ({ probabilityOfAction: v })} />
                      <RangeField label="Threat Capability" suffix="0-1" value={q.threatCapability} disabled={readOnly} onChange={(v) => setQ({ threatCapability: v })} />
                      <RangeField label="Control Strength" suffix="0-1" value={q.controlStrength} disabled={readOnly} onChange={(v) => setQ({ controlStrength: v })} />
                      <RangeField label="Primary Loss Magnitude" suffix="EUR" value={q.primaryLossMagnitude} disabled={readOnly} onChange={(v) => setQ({ primaryLossMagnitude: v })} />
                      <RangeField label="Secondary Loss Magnitude" suffix="EUR" value={q.secondaryLossMagnitude} disabled={readOnly} onChange={(v) => setQ({ secondaryLossMagnitude: v })} />
                    </>
                  );
                })()}
              </div>

              {!readOnly && (
                <button type="button" className="np-btn np-btn-primary mb-3" onClick={runSimulation} disabled={running}>
                  {running ? <><span className="spinner-border spinner-border-sm me-1"></span>Running 10,000 iterations...</> : <><i className="ri-play-line me-1"></i>Run Monte Carlo Simulation</>}
                </button>
              )}

              {assessment.results?.monteCarlo && (
                <div>
                  <div className="row g-3 mb-3">
                    <div className="col-6 col-md-3">
                      <div className="np-mini-stat">
                        <div className="np-mini-stat-value np-tabular">{formatCurrency(assessment.results.monteCarlo.annualLossExposure.p10)}</div>
                        <div className="np-mini-stat-label">ALE P10</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="np-mini-stat">
                        <div className="np-mini-stat-value np-tabular">{formatCurrency(assessment.results.monteCarlo.annualLossExposure.p50)}</div>
                        <div className="np-mini-stat-label">ALE P50 (median)</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="np-mini-stat">
                        <div className="np-mini-stat-value np-tabular">{formatCurrency(assessment.results.monteCarlo.annualLossExposure.p90)}</div>
                        <div className="np-mini-stat-label">ALE P90</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="np-mini-stat">
                        <div className="np-mini-stat-value np-tabular">{formatCurrency(assessment.results.monteCarlo.annualLossExposure.mean)}</div>
                        <div className="np-mini-stat-label">ALE Mean</div>
                      </div>
                    </div>
                  </div>
                  {histogramSeries.length > 0 && (
                    <ReactApexChart
                      type="bar"
                      height={220}
                      series={histogramSeries}
                      options={{
                        chart: { toolbar: { show: false } },
                        plotOptions: { bar: { columnWidth: '95%' } },
                        dataLabels: { enabled: false },
                        xaxis: { labels: { show: false }, title: { text: 'Simulated Annual Loss Exposure (low -> high)' } },
                        yaxis: { title: { text: 'Simulations' } },
                        title: { text: 'ALE Distribution (10,000 iterations)', style: { fontSize: '13px' } },
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
